import React from 'react';

import { Link } from 'react-router-dom';
import { AppBar, Grid, IconButton, Toolbar, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AccountCircleRounded } from '@material-ui/icons';

import CartButton from './CartButton.component';
import MainMenu from './MainMenu.component';
import CategoryNav from '../../../categories/components/public/CategoryNav.component';
import { useFindPublicCategories } from '../../../categories/hooks/public/find-categories.hook';
import { resolveHomeLink } from '../utils/resolve-home-link.util';
import { FLOATING_CATEGORY_NAV_HEIGHT } from '../constants/layout.constants';
import { usePanel } from '../hooks/use-panel.hook';
import { PUBLIC_PANELS } from '../constants/panel.constants';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: '#1c1c1c',
  },
  // Le rembourrage vit sur ce conteneur, pas sur le `Grid` lui-même : un `Grid container` avec
  // `spacing` porte des marges négatives et une largeur `calc(100% + n)`. Sur l'élément rembourré,
  // elles débordent de la barre et créent un défilement horizontal ; sur un parent, elles sont
  // absorbées par son rembourrage.
  toolbarRow: {
    backgroundColor: '#fff',
    padding: '12px 24px',
  },
  logo: {
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#1c1c1c',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  // Paire de contrôles serrée : le flex avec `gap` la gère sans les marges négatives d'un Grid
  // imbriqué, qui déborderaient de la barre.
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  accountButton: {
    minWidth: 44,
    minHeight: 44,
  },
  // Hors du flux, la bande laisse le contenu de la page défiler dessous : c'est ce qui rend le
  // fond de la page visible de part et d'autre de la pastille. `pointerEvents: none` est
  // indispensable, sinon cette bande transparente et pleine largeur intercepterait les clics
  // destinés au contenu qu'elle survole.
  floatingNav: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    padding: '12px 24px 0',
    pointerEvents: 'none',
    height: FLOATING_CATEGORY_NAV_HEIGHT,
  },
  // La barre se dimensionne sur ses catégories plutôt que sur la fenêtre : `fit-content` la fait
  // grandir avec le nombre de catégories actives, et le `maxWidth` la borne à la largeur utile
  // pour que `CategoryNav` reprenne son défilement horizontal au lieu de déborder.
  categoryRow: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem 5rem',
    borderRadius: '5px',
    backgroundColor: theme.palette.client.primary.main,
    boxShadow: '0 4px 14px rgba(23, 34, 74, 0.18)',
    width: 'fit-content',
    maxWidth: 'calc(100% - 48px)',
    margin: '0 auto',
  },
}));

const DesktopAppBar: React.FC = () => {
  const classes = useStyles();
  const { loading, categories } = useFindPublicCategories();
  const { openPanel } = usePanel();

  const homeLink = resolveHomeLink(loading, categories);

  return (
    <AppBar position="sticky" className={classes.appBar}>
      {/* Trois zones : le logo et les actions se dimensionnent sur leur contenu, le menu occupe
          l'espace restant. Les points de rupture se déclarent ici, pas dans la feuille de styles. */}
      <div className={classes.toolbarRow}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs="auto">
            <Typography component={Link} to={homeLink} className={classes.logo}>
              Panier Bio Paris
            </Typography>
          </Grid>

          <Grid item xs>
            <MainMenu />
          </Grid>

          <Grid item xs="auto">
            <div className={classes.actions}>
              <IconButton
                className={classes.accountButton}
                aria-label="Se connecter"
                onClick={() => openPanel(PUBLIC_PANELS.LOGIN)}
              >
                <AccountCircleRounded />
              </IconButton>
              <CartButton />
            </div>
          </Grid>
        </Grid>
      </div>

      <div className={classes.floatingNav}>
        <Toolbar variant="dense" className={classes.categoryRow} disableGutters>
          <CategoryNav />
        </Toolbar>
      </div>
    </AppBar>
  );
};

export default DesktopAppBar;
